import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import auditLogService from "../../services/auditLogService";

// В auditLogSlice.js - добавете този нов thunk след съществуващите
export const fetchAuditLogsForEntity = createAsyncThunk(
  "auditLogs/fetchForEntity",
  async (
    { entityId, entityType, page = 0, size = 20 },
    { rejectWithValue },
  ) => {
    try {
      const params = {
        page,
        size,
        sort: "performedAt,desc",
      };

      // Използваме различни endpoints в зависимост от entityType
      if (entityType === "CHITALISHTE") {
        const response = await auditLogService.getAuditLogsByChitalishte(
          entityId,
          page,
          size,
        );
        return response.data;
      } else if (entityType === "FEEDBACK") {
        const response = await auditLogService.getAuditLogsByFeedback(
          entityId,
          page,
          size,
        );
        return response.data;
      } else {
        // Общо търсене с филтри
        const response = await auditLogService.getAllAuditLogs({
          ...params,
          chitalishteId: entityType === "CHITALISHTE" ? entityId : undefined,
          entityType,
        });
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching audit logs for entity:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Грешка при зареждане на историята",
      );
    }
  },
);

// Основен thunk за зареждане на логове
export const fetchAuditLogs = createAsyncThunk(
  "auditLogs/fetchAll",
  async (
    {
      page = 0,
      size = 20,
      sortBy = "performedAt",
      sortDirection = "desc",
      filters = {},
      forceRefresh = null,
    },
    { rejectWithValue },
  ) => {
    try {
      // Подготвяме параметрите
      const params = {
        page,
        size,
        sortBy,
        sortDirection,
        ...filters,
      };

      // Добавяме timestamp за force refresh
      if (forceRefresh) {
        params._t = forceRefresh;
      }

      // Премахваме празни стойности от филтрите
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });

      const response = await auditLogService.getAllAuditLogs(params);
      return response.data;
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Грешка при зареждане на логове",
      );
    }
  },
);

// Thunk за статистики
export const fetchAuditLogStats = createAsyncThunk(
  "auditLogs/fetchStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await auditLogService.getAuditLogStats();
      return response.data;
    } catch (error) {
      console.error("Error fetching audit stats:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Грешка при зареждане на статистики",
      );
    }
  },
);

// Thunk за търсене с релации
export const searchAuditLogsWithRelations = createAsyncThunk(
  "auditLogs/searchWithRelations",
  async ({ filters = {}, page = 0, size = 20 }, { rejectWithValue }) => {
    try {
      const params = {
        page,
        size,
        ...filters,
      };

      // Премахваме празни стойности
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });

      const response =
        await auditLogService.searchAuditLogsWithRelations(params);
      return response.data;
    } catch (error) {
      console.error("Error searching audit logs:", error);
      return rejectWithValue(
        error.response?.data?.message || error.message || "Грешка при търсене",
      );
    }
  },
);

const auditLogSlice = createSlice({
  name: "auditLogs",
  initialState: {
    logs: [],
    stats: null,
    loading: false,
    error: null,
    pagination: {
      page: 0,
      size: 20,
      totalPages: 0,
      totalElements: 0,
    },
    filters: {
      chitalishteId: "",
      feedbackId: "",
      entityType: "",
      action: "",
      performedBy: "",
      startDate: "",
      endDate: "",
    },
  },
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        chitalishteId: "",
        feedbackId: "",
        entityType: "",
        action: "",
        performedBy: "",
        startDate: "",
        endDate: "",
      };
      // Нулираме страницата при изчистване на филтрите
      state.pagination.page = 0;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetAuditLogState: (state) => {
      state.logs = [];
      state.stats = null;
      state.error = null;
      state.pagination = {
        page: 0,
        size: 20,
        totalPages: 0,
        totalElements: 0,
      };
      state.filters = {
        chitalishteId: "",
        feedbackId: "",
        entityType: "",
        action: "",
        performedBy: "",
        startDate: "",
        endDate: "",
      };
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.pagination.size = action.payload;
      state.pagination.page = 0; // Reset to first page when rows per page changes
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuditLogsForEntity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuditLogsForEntity.fulfilled, (state, action) => {
        state.loading = false;

        if (Array.isArray(action.payload)) {
          state.logs = action.payload;
          state.pagination = {
            page: 0,
            size: action.payload.length,
            totalPages: 1,
            totalElements: action.payload.length,
          };
        } else if (action.payload.content) {
          state.logs = action.payload.content;
          state.pagination = {
            page: action.payload.pageable?.pageNumber || 0,
            size: action.payload.pageable?.pageSize || 20,
            totalPages: action.payload.totalPages || 1,
            totalElements: action.payload.totalElements || 0,
          };
        } else {
          state.logs = action.payload || [];
          state.pagination = {
            page: 0,
            size: 20,
            totalPages: 1,
            totalElements: (action.payload || []).length,
          };
        }
      })
      .addCase(fetchAuditLogsForEntity.rejected, (state, action) => {
        state.loading = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : JSON.stringify(action.payload);
      })
      // fetchAuditLogs
      .addCase(fetchAuditLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuditLogs.fulfilled, (state, action) => {
        state.loading = false;

        if (Array.isArray(action.payload)) {
          state.logs = action.payload;
          state.pagination = {
            page: 0,
            size: action.payload.length,
            totalPages: 1,
            totalElements: action.payload.length,
          };
        } else if (action.payload.content) {
          state.logs = action.payload.content;
          state.pagination = {
            page: action.payload.pageable?.pageNumber || 0,
            size: action.payload.pageable?.pageSize || 20,
            totalPages: action.payload.totalPages || 1,
            totalElements: action.payload.totalElements || 0,
          };
        } else {
          state.logs = action.payload || [];
          state.pagination = {
            page: 0,
            size: 20,
            totalPages: 1,
            totalElements: (action.payload || []).length,
          };
        }
      })
      .addCase(fetchAuditLogs.rejected, (state, action) => {
        state.loading = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : JSON.stringify(action.payload);
      })

      // fetchAuditLogStats
      .addCase(fetchAuditLogStats.pending, (state) => {
        // Не променяме loading state за статистики
        state.error = null;
      })
      .addCase(fetchAuditLogStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(fetchAuditLogStats.rejected, (state, action) => {
        state.stats = null;
        state.error = action.payload;
      })

      // searchAuditLogsWithRelations
      .addCase(searchAuditLogsWithRelations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchAuditLogsWithRelations.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.content) {
          state.logs = action.payload.content;
          state.pagination = {
            page: action.payload.pageable?.pageNumber || 0,
            size: action.payload.pageable?.pageSize || 20,
            totalPages: action.payload.totalPages || 1,
            totalElements: action.payload.totalElements || 0,
          };
        } else {
          state.logs = action.payload || [];
        }
      })
      .addCase(searchAuditLogsWithRelations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  clearError,
  resetAuditLogState,
  setPage,
  setRowsPerPage,
} = auditLogSlice.actions;

export const selectAuditLogs = (state) => state.auditLogs.logs || [];
export const selectAuditLogStats = (state) => state.auditLogs.stats;
export const selectAuditLogLoading = (state) => state.auditLogs.loading;
export const selectAuditLogError = (state) => state.auditLogs.error;
export const selectAuditLogPagination = (state) => state.auditLogs.pagination;
export const selectAuditLogFilters = (state) => state.auditLogs.filters;
export const fetchAuditLogsByChitalishte = (
  chitalishteId,
  page = 0,
  size = 20,
) => auditLogService.getAuditLogsByChitalishte(chitalishteId, page, size);
export const fetchAuditLogsByChitalishteWithFeedback = (
  chitalishteId,
  page = 0,
  size = 20,
) =>
  auditLogService.getAuditLogsByChitalishteWithFeedback(
    chitalishteId,
    page,
    size,
  );

export default auditLogSlice.reducer;
